import { useEffect, useState, useMemo } from 'react';
import './styles.css';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { LayoutHeader as Header, LayoutFooter as Footer, LayoutVehicleCard } from '@/layouts';
import { ServiceModal } from '@/components/ServiceModal';
import { VehicleSection } from '@/components/VehicleSection';
import { extractIdFromSlug, generateVehicleSlug } from '@/lib/slug';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { secureGetItem, secureSetItem } from '@/lib/storage';
import { openWhatsApp } from '@/lib/whatsapp';
import SEOHead from '@/components/SEOHead';
import { generateVehicleStructuredData } from '@/lib/structuredData';
import {
  MessageCircle,
  Heart,
  Share2,
  Calendar,
  Gauge,
  Fuel,
  Settings,
  Palette,
  Car,
  Shield,
  Phone,
  MapPin,
  Building2,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Eye,
  Star,
  CreditCard,
  Maximize,
  X,
  Send,
  File,
  FilePen,
  Navigation
} from 'lucide-react';

import { useAppData } from '@/contexts/AppDataContext';
import { useTheme } from '@/contexts/ThemeContext';
import { layout4ThemeStyle } from './theme';
import { useStore } from '@/contexts/StoreContext';
import { useToast } from '@/hooks/use-toast';
import { requestUserLocation, getDistanceToStore, formatDistance } from '@/lib/geolocation';
import { VehicleNotFound } from '@/components/VehicleNotFound';
import { trackVehicleView, trackLeadSubmission, trackWhatsAppClick, trackAddToFavorites } from '@/lib/analytics';
import { openStoreDirections } from '@/lib/storeMapUrls';

// Lead Form Component (for API type and message type)
const LeadForm = ({ vehicle, config, buttonColor, store, serviceType = 'message', serviceTitle = 'Envie uma mensagem', service }: { vehicle: any; config: any; buttonColor: string; store: any; serviceType?: string; serviceTitle?: string; service?: any }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const isFinancingLead = serviceType === '65dcb7898eca108d87c91dbc';

  // Pre-fill message with vehicle info on mount
  useEffect(() => {
    let vehicleInfo = '';

    if (serviceType === '65dcb7898eca108d87c91dbc') {
      // Financing service
      vehicleInfo = `Olá,\n\nGostaria de simular um financiamento para o veículo:\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}\nPreço: ${formatPrice(vehicle.price)}\n\nPor favor, entrem em contato para mais informações sobre as condições de financiamento.`;
    } else {
      // Default message service
      vehicleInfo = `Olá,\n\nTenho interesse no veículo:\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}\nPreço: ${formatPrice(vehicle.price)}\n\nGostaria de receber mais informações.`;
    }

    setFormData(prev => ({
      ...prev,
      message: vehicleInfo
    }));
  }, [vehicle, serviceType]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
    } else {
      return numbers.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
    }
  };

  const handlePhoneChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    handleInputChange('phone', formatted);
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast({
        title: '⚠️ Campo obrigatório',
        description: 'Por favor, preencha o campo Nome.',
        variant: 'destructive'
      });
      return false;
    }

    if (!formData.email.trim()) {
      toast({
        title: '⚠️ Campo obrigatório',
        description: 'Por favor, preencha o campo E-mail.',
        variant: 'destructive'
      });
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: '❌ E-mail inválido',
        description: 'Por favor, insira um endereço de e-mail válido.',
        variant: 'destructive'
      });
      return false;
    }

    if (!formData.phone.trim()) {
      toast({
        title: '⚠️ Campo obrigatório',
        description: 'Por favor, preencha o campo Telefone.',
        variant: 'destructive'
      });
      return false;
    }

    const phoneNumbers = formData.phone.replace(/\D/g, '');
    if (phoneNumbers.length < 10) {
      toast({
        title: '❌ Telefone inválido',
        description: 'Por favor, insira um número de telefone válido com DDD.',
        variant: 'destructive'
      });
      return false;
    }

    if (!formData.message.trim()) {
      toast({
        title: '⚠️ Campo obrigatório',
        description: 'Por favor, preencha o campo Mensagem.',
        variant: 'destructive'
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Check if service has API type
      const isApiType = (service?.lead?.type === 'api' || service?.lead?.type === 'email');

      if (isApiType) {
        // Send via API
        const { LEAD } = await import('@/lib/api');

        // Prepare lead data following the correct format
        const leadData = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone.replace(/\D/g, ''), // Remove formatting
          message: formData.message,
          vehicle: `${vehicle.brand} ${vehicle.model} ${vehicle.version ? `${vehicle.version} ` : ' '}${vehicle.manufactureYear}/${vehicle.modelYear}`,
          id: vehicle.id,
          plaque: vehicle.plaque || '',
          brand: vehicle.brand,
          model: vehicle.model,
          version: vehicle.version || '',
          year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
          price: formatPrice(vehicle.price),
          mileage: `${vehicle.mileage}km`
        };

        const response = await LEAD(serviceType, store, leadData);

        if (response?.status == 200) {
          // Track lead submission
          trackLeadSubmission({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            message: formData.message,
            serviceType,
            vehicle: {
              id: vehicle.id,
              brand: vehicle.brand,
              model: vehicle.model,
              version: vehicle.version || '',
              year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
              price: vehicle.price,
              mileage: vehicle.mileage
            }
          });

          toast({
            title: 'Mensagem enviada!',
            description: 'Entraremos em contato em breve.',
          });

          // Reset form
          setFormData({
            name: '',
            email: '',
            phone: '',
            message: `Olá,\n\nTenho interesse no veículo:\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}\nPreço: ${formatPrice(vehicle.price)}\n\nGostaria de receber mais informações.`
          });
        } else {
          toast({
            title: 'Erro ao enviar',
            description: 'Tente novamente.',
            variant: 'destructive'
          });
        }
      } else {
        // Send via WhatsApp (type === 'message')
        toast({
          title: '✅ Redirecionando',
          description: 'Abrindo WhatsApp...',
        });

        const { openWhatsApp } = await import('@/lib/whatsapp');

        if (service?.lead?.ios) {
          // Build message from template (same logic as ServiceModal)
          let message = service.lead?.ios;
          const whatsappTemplate = message.substring(0, message.indexOf('&text=') + 6);
          message = message.substring(message.indexOf('&text=') + 6);

          // Prepare form data with vehicle info
          const messageData: Record<string, string> = {
            nome: formData.name,
            name: formData.name,
            email: formData.email,
            telefone: formData.phone,
            phone: formData.phone,
            mensagem: formData.message,
            message: formData.message,
            veiculo: `${vehicle.brand} ${vehicle.model} ${vehicle.version ? `${vehicle.version} ` : ''}${vehicle.manufactureYear}/${vehicle.modelYear}`,
            vehicle: `${vehicle.brand} ${vehicle.model} ${vehicle.version ? `${vehicle.version} ` : ''}${vehicle.manufactureYear}/${vehicle.modelYear}`,
            marca: vehicle.brand,
            brand: vehicle.brand,
            modelo: vehicle.model,
            model: vehicle.model,
            versao: vehicle.version || '',
            version: vehicle.version || '',
            ano: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
            year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
            preco: formatPrice(vehicle.price),
            price: formatPrice(vehicle.price),
            km: `${vehicle.mileage}km`,
            mileage: `${vehicle.mileage}km`
          };

          // Replace placeholders with form data
          Object.entries(messageData).forEach(([key, value]) => {
            const regex = new RegExp(`\\{${key}\\}`, 'g');
            message = message.replace(regex, value);
          });

          // Add current date/time if needed
          const now = new Date();
          message = message.replace(/\{datahora\}/g, now.toLocaleString('pt-BR'));
          message = message.replace(/\{datetime\}/g, now.toLocaleString('pt-BR'));

          const whatsappUrl = whatsappTemplate + encodeURIComponent(message);
          openWhatsApp(whatsappUrl);
        } else {
          // Use default WhatsApp with message
          const phoneMatch = store.whatsapp?.ios?.match(/phone=([^&]+)/);
          const phone = phoneMatch ? phoneMatch[1] : '';
          openWhatsApp(`whatsapp://send?phone=${phone}&text=${encodeURIComponent(formData.message)}`);
        }

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: `Olá,\n\nTenho interesse no veículo:\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}\nPreço: ${formatPrice(vehicle.price)}\n\nGostaria de receber mais informações.`
        });
      }
    } catch (error: any) {
      console.error('Error submitting lead:', error);

      toast({
        title: 'Erro ao enviar',
        description: error?.message || 'Ocorreu um erro ao enviar sua mensagem. Tente novamente.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">

        <CardTitle className="flex items-center gap-2">
          {serviceType === 'message' ? (
            <MessageCircle className="h-5 w-5 text-primary" />
          ) : (
            <CreditCard className="h-5 w-5 text-primary" />
          )}
          {serviceTitle}
        </CardTitle>

        {/* Name Field */}
        <div className="space-y-2">
          <Label htmlFor="name" className="text-sm font-semibold">
            Nome Completo <span className="text-red-500">*</span>
          </Label>
          <Input
            id="name"
            placeholder="Digite seu nome completo"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className="h-11"
          />
        </div>

        {/* Email Field */}
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-semibold">
            E-mail <span className="text-red-500">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="seu@email.com"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className="h-11"
          />
        </div>

        {/* Phone Field */}
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-sm font-semibold">
            Telefone <span className="text-red-500">*</span>
          </Label>
          <Input
            id="phone"
            type="tel"
            placeholder="(11) 99999-9999"
            value={formData.phone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            maxLength={15}
            className="h-11"
          />
        </div>

        {/* Message Field — hidden on vehicle detail financing (vehicle already on page) */}
        {!isFinancingLead && (
          <div className="space-y-2">
            <Label htmlFor="message" className="text-sm font-semibold">
              Mensagem <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="message"
              placeholder="Digite sua mensagem..."
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              rows={5}
              className="resize-none"
            />
          </div>
        )}

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full h-12 text-base font-semibold text-white shadow-lg hover:shadow-xl transition-all duration-300"
          style={{
            background: `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`,
          }}
          onMouseEnter={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}ee, ${buttonColor})`;
            }
          }}
          onMouseLeave={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`;
            }
          }}
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
              Enviando...
            </>
          ) : (
            <>
              <Send className="h-5 w-5 mr-2" />
              Enviar
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

const VehicleDetail = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { config, vehicles, isLoading } = useAppData();
  const { selectedStore, availableStores } = useStore();

  // Extract slug from params (can be :id or carro/*, moto/*, caminhao/*)
  const slugOrId = params.id || params['*'] || '';
  const [vehicle, setVehicle] = useState<any>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorited, setIsFavorited] = useState(false);
  const [relatedVehicles, setRelatedVehicles] = useState<any[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenImageIndex, setFullscreenImageIndex] = useState(0);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [isFullscreenImageLoading, setIsFullscreenImageLoading] = useState(false);
  const [relatedSlugs, setRelatedSlugs] = useState<Record<number, string>>({});
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const { colors } = useTheme();
  const themeStyle = layout4ThemeStyle(colors);
  const { toast } = useToast();

  const buttonColor = colors.button;
  const primaryColor = colors.primary;
  const secondaryColor = colors.secondary;
  const titleColor = colors.title;
  const textColor = colors.text;

  // Get the store for this vehicle (based on vehicle.store field)
  const vehicleStore = useMemo(() => {
    if (!vehicle) {
      return selectedStore || config.stores[0];
    }
    const storeId = vehicle.store;
    if (!storeId) {
      return selectedStore || config.stores[0];
    }
    return config.stores?.find((s: any) => s._id === storeId) || config.stores[0];
  }, [vehicle, config.stores, selectedStore]);

  // Request user location only when needed (user interaction)
  // Don't request automatically on page load to avoid trust issues

  useEffect(() => {
    const loadVehicleData = async () => {
      // Only check for vehicle after data has loaded
      if (!isLoading && vehicles.length > 0) {
        // Extract ID from slug or use direct ID
        const vehicleId = await extractIdFromSlug(slugOrId || '') || slugOrId;
        const foundVehicle = vehicles.find(v => v.id.toString() === vehicleId);
        if (foundVehicle) {
          setVehicle(foundVehicle);

          // Track vehicle view
          trackVehicleView({
            id: foundVehicle.id,
            brand: foundVehicle.brand,
            model: foundVehicle.model,
            version: foundVehicle.version || '',
            year: `${foundVehicle.manufactureYear}/${foundVehicle.modelYear}`,
            price: foundVehicle.price,
            mileage: foundVehicle.mileage
          });

          // Reset to first image when vehicle changes
          setCurrentImageIndex(0);

          // Register vehicle as recently viewed
          try {
            const viewedStr = await secureGetItem('recentlyViewedVehicles');
            let viewedVehicles = viewedStr ? JSON.parse(viewedStr) : [];

            // Remove if already exists (to avoid duplicates)
            viewedVehicles = viewedVehicles.filter((v: any) => v.id !== foundVehicle.id);

            // Add to beginning of array with timestamp
            viewedVehicles.unshift({
              id: foundVehicle.id,
              timestamp: Date.now()
            });

            // Keep only last 20 vehicles
            viewedVehicles = viewedVehicles.slice(0, 20);

            await secureSetItem('recentlyViewedVehicles', JSON.stringify(viewedVehicles));

            // Dispatch event to notify other components
            window.dispatchEvent(new Event('vehicleViewed'));
          } catch (error) {
            console.error('Erro ao registrar veículo visualizado:', error);
          }

          // Check if vehicle is favorited
          const favorites = await secureGetItem('favorites');
          if (favorites && Array.isArray(favorites)) {
            setIsFavorited(favorites.includes(foundVehicle.id));
          }

          // Determine if we should filter by store based on unifiedAds and unifiedAdsStore
          const unifiedAds = config?.unifiedAds !== false;
          const unifiedAdsStore = config?.unifiedAdsStore;
          const vehicleStoreId = foundVehicle.store;

          let filterByStore = false;

          if (!unifiedAds) {
            // Rule 1: unifiedAds is false → always filter by vehicle's store
            filterByStore = true;
          } else if (unifiedAdsStore !== null && unifiedAdsStore !== undefined) {
            // Rule 3 & 4: unifiedAds is true and unifiedAdsStore is set
            // Only show all vehicles if vehicle's store is the unifiedAdsStore
            filterByStore = vehicleStoreId !== unifiedAdsStore;
          }
          // Rule 2: unifiedAds is true and unifiedAdsStore is null → don't filter (filterByStore = false)

          // Find related vehicles with priority: 1) same model, 2) same brand, 3) similar price
          // Filter by store if needed based on unifiedAds rules
          const sameModel = vehicles.filter(v =>
            v.id !== foundVehicle.id &&
            v.model === foundVehicle.model &&
            (!filterByStore || v.store === vehicleStoreId)
          );

          const sameBrand = vehicles.filter(v =>
            v.id !== foundVehicle.id &&
            v.brand === foundVehicle.brand &&
            v.model !== foundVehicle.model &&
            (!filterByStore || v.store === vehicleStoreId)
          );

          // Others: similar price range (±10,000)
          const priceMin = foundVehicle.price - 10000;
          const priceMax = foundVehicle.price + 10000;
          const others = vehicles.filter(v =>
            v.id !== foundVehicle.id &&
            v.brand !== foundVehicle.brand &&
            v.price >= priceMin &&
            v.price <= priceMax &&
            (!filterByStore || v.store === vehicleStoreId)
          );

          // Show only the highest priority available (don't mix)
          let related: any[] = [];
          if (sameModel.length > 0) {
            // Priority 1: Show only same model
            related = sameModel.slice(0, 4);
          } else if (sameBrand.length > 0) {
            // Priority 2: Show only same brand
            related = sameBrand.slice(0, 4);
          } else {
            // Priority 3: Show only similar price
            related = others.slice(0, 4);
          }

          setRelatedVehicles(related);
        }
        setHasLoaded(true);
      }
    };

    loadVehicleData();
  }, [slugOrId, vehicles, isLoading]);

  // Load slugs for related vehicles
  useEffect(() => {
    const loadRelatedSlugs = async () => {
      const newSlugs: Record<number, string> = {};
      for (const relatedVehicle of relatedVehicles) {
        newSlugs[relatedVehicle.id] = await generateVehicleSlug(relatedVehicle);
      }
      setRelatedSlugs(newSlugs);
    };
    if (relatedVehicles.length > 0) {
      loadRelatedSlugs();
    }
  }, [relatedVehicles]);

  // Handle keyboard navigation in fullscreen
  useEffect(() => {
    if (!vehicle) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isFullscreen) return;

      if (e.key === 'Escape') {
        setIsFullscreen(false);
      } else if (e.key === 'ArrowRight') {
        setFullscreenImageIndex((prev) =>
          prev === vehicle.photos.length - 1 ? 0 : prev + 1
        );
      } else if (e.key === 'ArrowLeft') {
        setFullscreenImageIndex((prev) =>
          prev === 0 ? vehicle.photos.length - 1 : prev - 1
        );
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, vehicle]);

  // Show loading state
  if (isLoading || !hasLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  // Show not found only after loading is complete
  if (!vehicle && hasLoaded) {
    return (
      <VehicleNotFound
        config={config}
        buttonColor={buttonColor}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        titleColor={titleColor}
        textColor={textColor}
      />
    );
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const formatMileage = (mileage: number) => {
    if (mileage === 0) return 'Zero KM';
    return new Intl.NumberFormat('pt-BR').format(mileage) + ' km';
  };

  const getVehicleTypeLabel = (type?: number) => {
    switch (type) {
      case 2: return 'Moto';
      case 3: return 'Caminhão';
      default: return 'Carro';
    }
  };

  const isFeatured = !!vehicle.changed?.featured;
  const hasDiscount = vehicle.originalPrice && vehicle.originalPrice > vehicle.price;
  const discountPercent = hasDiscount
    ? Math.round((1 - vehicle.price / vehicle.originalPrice) * 100)
    : 0;

  const nextImage = () => {
    if (isImageLoading) return; // Block if loading
    setIsImageLoading(true);
    setCurrentImageIndex((prev) =>
      prev === vehicle.photos.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    if (isImageLoading) return; // Block if loading
    setIsImageLoading(true);
    setCurrentImageIndex((prev) =>
      prev === 0 ? vehicle.photos.length - 1 : prev - 1
    );
  };

  // Handle main image load complete
  const handleMainImageLoad = () => {
    setIsImageLoading(false);
  };

  const toggleFavorite = async () => {
    const favorites = await secureGetItem('favorites') || [];
    let updatedFavorites = Array.isArray(favorites) ? [...favorites] : [];

    if (updatedFavorites.includes(vehicle.id)) {
      // Remove from favorites
      updatedFavorites = updatedFavorites.filter((id: number) => id !== vehicle.id);
      setIsFavorited(false);
    } else {
      // Add to favorites
      updatedFavorites.push(vehicle.id);
      setIsFavorited(true);

      // Track add to favorites
      trackAddToFavorites({
        id: vehicle.id,
        brand: vehicle.brand,
        model: vehicle.model,
        version: vehicle.version || '',
        year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
        price: vehicle.price,
        mileage: vehicle.mileage
      });
    }

    await secureSetItem('favorites', updatedFavorites);

    // Dispatch custom event to update other components
    window.dispatchEvent(new Event('favoritesUpdated'));
  };

  const handleWhatsApp = () => {
    const adLead = vehicleStore.ad?.lead;

    // Check if lead type is "message" and has custom template
    if (adLead?.type === 'message' && adLead?.ios) {
      let message = adLead?.ios;
      message = message.substr(message.indexOf('&text=') + 6); // Extract message after 'text='

      // Replace dynamic placeholders based on replace array
      if (adLead.replace && Array.isArray(adLead.replace)) {
        adLead.replace.forEach((replaceItem: any) => {
          Object.keys(replaceItem).forEach((placeholder) => {
            const field = replaceItem[placeholder];
            let value = '';

            // Map field names to vehicle properties
            switch (field) {
              case 'vehicle':
                value = `${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}`;
                break;
              case 'price':
                value = formatPrice(vehicle.price);
                break;
              case 'brand':
                value = vehicle.brand;
                break;
              case 'model':
                value = vehicle.model;
                break;
              case 'version':
                value = vehicle.version;
                break;
              case 'year':
              case 'modelYear':
                value = vehicle.modelYear.toString();
                break;
              case 'mileage':
                value = `${vehicle.mileage.toLocaleString('pt-BR')} km`;
                break;
              case 'color':
                value = vehicle.color;
                break;
              case 'fuel':
                value = vehicle.fuel;
                break;
              case 'transmission':
                value = vehicle.transmission;
                break;
              default:
                // Try to get value directly from vehicle object
                value = vehicle[field] || '';
            }

            // Replace placeholder in message (case insensitive)
            const regex = new RegExp(`\\{${placeholder}\\}`, 'gi');
            message = message.replace(regex, value);
          });
        });
      }

      // Extract phone number from whatsapp?.ios URL
      const phoneMatch = vehicleStore.ad?.lead?.ios?.match(/phone=([^&]+)/);
      const phone = phoneMatch ? phoneMatch[1] : '';

      // Build WhatsApp URL
      const url = `whatsapp://send?phone=${phone}&text=${encodeURIComponent(message)}`;

      // Track WhatsApp click
      trackWhatsAppClick('vehicle_detail', {
        id: vehicle.id,
        brand: vehicle.brand,
        model: vehicle.model,
        version: vehicle.version || '',
        year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
        price: vehicle.price,
        mileage: vehicle.mileage
      });

      // Open WhatsApp with fallback to web version
      openWhatsApp(url);
    } else {
      // Default behavior (fallback)
      const message = `Olá,\n\nTenho interesse no veículo abaixo e gostaria de receber mais informações.\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version}, ${formatPrice(vehicle.price)}\n\nFico no aguardo,`;
      const url = `${vehicleStore.ad?.lead?.ios}&text=${encodeURIComponent(message)}`;

      // Track WhatsApp click
      trackWhatsAppClick('vehicle_detail', {
        id: vehicle.id,
        brand: vehicle.brand,
        model: vehicle.model,
        version: vehicle.version || '',
        year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
        price: vehicle.price,
        mileage: vehicle.mileage
      });

      // Open WhatsApp with fallback to web version
      openWhatsApp(url);
    }
  };

  // Find financing service from vehicle's store
  const financingService = vehicleStore?.services?.find(
    (s: any) => s.service === '65dcb7898eca108d87c91dbc'
  );

  // Find test-drive service from vehicle's store
  const testDriveService = vehicleStore?.services?.find(
    (s: any) => s.service === '65dcb5e78eca108d87c91db8'
  );

  // Check if test-drive should be shown
  const showTestDrive = vehicleStore?.ad?.showTestDriveOption && testDriveService;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${vehicle.brand} ${vehicle.model} ${vehicle.version}`,
          text: `Confira este ${vehicle.brand} ${vehicle.model} por ${formatPrice(vehicle.price)}`,
          url: window.location.href,
        });
      } catch (err) { }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: '✅ Link copiado',
        description: 'O link do veículo foi copiado para a área de transferência.',
      });
    }
  };

  const openFullscreen = (index: number) => {
    setFullscreenImageIndex(index);
    setIsFullscreen(true);
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
  };

  const nextFullscreenImage = () => {
    if (isFullscreenImageLoading) return; // Block if loading
    setIsFullscreenImageLoading(true);
    setFullscreenImageIndex((prev) =>
      prev === vehicle.photos.length - 1 ? 0 : prev + 1
    );
  };

  const prevFullscreenImage = () => {
    if (isFullscreenImageLoading) return; // Block if loading
    setIsFullscreenImageLoading(true);
    setFullscreenImageIndex((prev) =>
      prev === 0 ? vehicle.photos.length - 1 : prev - 1
    );
  };

  // Handle fullscreen image load complete
  const handleFullscreenImageLoad = () => {
    setIsFullscreenImageLoading(false);
  };

  const shareUrl = `${window.location.origin}${location.pathname}${location.search}`;
  const canonicalVehicleUrl = `${window.location.origin}${location.pathname}`;
  const seoVehicleTitle = `${vehicle.brand} ${vehicle.model}${vehicle.version ? ` ${vehicle.version}` : ''} ${vehicle.modelYear}`;

  return (
    <div className="layout-4 min-h-screen bg-[var(--wl4-bg)]" style={themeStyle}>
      <SEOHead
        title={seoVehicleTitle}
        description={`${vehicle.brand} ${vehicle.model}${vehicle.version ? ` ${vehicle.version}` : ''} ${vehicle.modelYear} — ${vehicle.color} — ${vehicle.manufactureYear}/${vehicle.modelYear} — ${vehicle.fuel} — ${vehicle.transmission} — ${vehicle.mileage.toLocaleString('pt-BR')} km — ${formatPrice(vehicle.price)}`}
        keywords={`${vehicle.brand}, ${vehicle.model}, ${vehicle.modelYear}, ${vehicle.fuel}, ${vehicle.transmission}, ${vehicle.color}, veículo usado, carro seminovo`}
        image={vehicle.photos?.[0]}
        imageAlt={seoVehicleTitle}
        type="product"
        url={shareUrl}
        canonicalUrl={canonicalVehicleUrl}
        structuredData={generateVehicleStructuredData({
          vehicle,
          config,
          store: vehicleStore,
          vehiclePageUrl: shareUrl
        })}
      />
      <Header config={config} />

      {/* Motorx-style detail header */}
      <section className="wl4-detail-head">
        <div className="wl4-container">
          <div className="wl4-detail-head-toolbar">
            <nav className="wl4-detail-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Início</Link>
              <span>/</span>
              <Link to="/estoque">Estoque</Link>
              <span>/</span>
              <span className="wl4-detail-breadcrumb-current">{vehicle.brand} {vehicle.model}</span>
            </nav>
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              size="sm"
              className="border-[var(--wl4-border)] text-[var(--wl4-text)] hover:bg-[var(--wl4-bg)] shrink-0"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
          </div>

          <div className="wl4-detail-tags">
            {hasDiscount && discountPercent > 0 && (
              <span className="wl4-detail-tag wl4-detail-tag--accent">-{discountPercent}%</span>
            )}
            {vehicle.isZeroKm && (
              <span className="wl4-detail-tag wl4-detail-tag--accent">Zero KM</span>
            )}
            {isFeatured && (
              <span className="wl4-detail-tag wl4-detail-tag--accent">Destaque</span>
            )}
            <span className="wl4-detail-tag">Marca: {vehicle.brand}</span>
            <span className="wl4-detail-tag">Modelo: {vehicle.model}</span>
            {vehicle.fuel && (
              <span className="wl4-detail-tag">{vehicle.fuel}</span>
            )}
          </div>

          <div className="wl4-detail-head-row mt-4">
            <div>
              <h1 className="wl4-detail-title" style={{ color: titleColor }}>
                {vehicle.brand} {vehicle.model} {vehicle.modelYear}
              </h1>
              {vehicle.version && (
                <p className="wl4-detail-version" style={{ color: textColor }}>{vehicle.version}</p>
              )}
              {vehicleStore?.place?.address && (
                <p className="wl4-detail-location">
                  <MapPin className="h-4 w-4 shrink-0" />
                  {vehicleStore.place.address}
                </p>
              )}
            </div>

            <div className="wl4-detail-head-side">
              <div className="wl4-detail-price-block">
                {hasDiscount && (
                  <span className="wl4-detail-price-old">{formatPrice(vehicle.originalPrice)}</span>
                )}
                <span className="wl4-detail-price">{formatPrice(vehicle.price)}</span>
              </div>
              <div className="wl4-detail-actions">
                <button
                  type="button"
                  onClick={toggleFavorite}
                  className={`wl4-detail-action-btn ${isFavorited ? 'is-active' : ''}`}
                  title={isFavorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                  aria-label={isFavorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                >
                  <Heart className={`h-5 w-5 ${isFavorited ? 'fill-current' : ''}`} />
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="wl4-detail-action-btn"
                  title="Compartilhar"
                  aria-label="Compartilhar"
                >
                  <Share2 className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="wl4-detail-body">
        <div className="wl4-container">
          <div className="wl4-detail-grid">
            <div className="wl4-detail-main">
              {/* Image Gallery */}
              <div className="wl4-detail-panel wl4-detail-gallery">
                <div className="relative">
                  <div className="wl4-detail-gallery-main group">
                    {isImageLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-muted/50 z-30">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
                      </div>
                    )}

                    <img
                      src={vehicle.photos[currentImageIndex]}
                      alt={`${vehicle.brand} ${vehicle.model}`}
                      className={`transition-opacity duration-300 ${isImageLoading ? 'opacity-50' : 'opacity-100'}`}
                      onLoad={handleMainImageLoad}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />

                    {vehicle.photos.length > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={prevImage}
                          disabled={isImageLoading}
                          onMouseEnter={(e) => e.currentTarget.parentElement?.classList.add('has-control-hover')}
                          onMouseLeave={(e) => e.currentTarget.parentElement?.classList.remove('has-control-hover')}
                          className={`wl4-detail-gallery-nav wl4-detail-gallery-nav--prev ${isImageLoading ? 'cursor-not-allowed' : ''}`}
                          aria-label="Foto anterior"
                        >
                          <ChevronLeft className="h-6 w-6" />
                        </button>
                        <button
                          type="button"
                          onClick={nextImage}
                          disabled={isImageLoading}
                          onMouseEnter={(e) => e.currentTarget.parentElement?.classList.add('has-control-hover')}
                          onMouseLeave={(e) => e.currentTarget.parentElement?.classList.remove('has-control-hover')}
                          className={`wl4-detail-gallery-nav wl4-detail-gallery-nav--next ${isImageLoading ? 'cursor-not-allowed' : ''}`}
                          aria-label="Próxima foto"
                        >
                          <ChevronRight className="h-6 w-6" />
                        </button>
                      </>
                    )}

                    <div className="wl4-detail-gallery-counter">
                      {currentImageIndex + 1} / {vehicle.photos.length}
                    </div>

                    <button
                      type="button"
                      onClick={() => !isImageLoading && openFullscreen(currentImageIndex)}
                      disabled={isImageLoading}
                      className={`absolute inset-0 bg-black/0 transition-colors flex items-center justify-center z-[5] group-[.has-control-hover]:pointer-events-none ${isImageLoading ? 'pointer-events-none' : 'hover:bg-black/20'}`}
                      aria-label="Ver em tela cheia"
                    >
                      <div className={`transition-opacity bg-white/90 backdrop-blur-sm rounded-full p-4 ${isImageLoading ? 'opacity-0' : 'opacity-0 group-hover:opacity-100 group-[.has-control-hover]:opacity-0'}`}>
                        <Maximize className="h-8 w-8 text-gray-800" />
                      </div>
                    </button>

                    <div className="wl4-detail-gallery-badge">
                      {vehicle.isZeroKm && (
                        <Badge className="bg-accent text-accent-foreground">Zero KM</Badge>
                      )}
                    </div>
                  </div>

                  {vehicle.photos.length > 1 && (
                    <div className="wl4-detail-thumbs">
                      {vehicle.photos.slice(0, vehicle.photos.length > 6 ? 5 : 6).map((photo: string, index: number) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => setCurrentImageIndex(index)}
                          className={`wl4-detail-thumb ${index === currentImageIndex ? 'is-active' : ''}`}
                          aria-label={`Ver foto ${index + 1}`}
                        >
                          <img src={photo} alt={`Foto ${index + 1}`} />
                        </button>
                      ))}
                      {vehicle.photos.length > 6 && (
                        <button
                          type="button"
                          onClick={() => openFullscreen(currentImageIndex)}
                          className="wl4-detail-thumb-more"
                          aria-label="Ver mais fotos"
                        >
                          <Maximize className="h-5 w-5" />
                          <span>+{vehicle.photos.length - 5}</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Car Overview */}
              <div className="wl4-detail-panel">
                <div className="wl4-detail-panel-head">
                  <h2 className="wl4-detail-panel-title">Visão geral</h2>
                </div>
                <div className="wl4-detail-panel-body !p-0">
                  <div className="wl4-detail-overview">
                    {(vehicle.manufactureYear || vehicle.modelYear) && (
                      <div className="wl4-detail-overview-item">
                        <span className="wl4-detail-overview-label">Ano</span>
                        <span className="wl4-detail-overview-value">{vehicle.manufactureYear}/{vehicle.modelYear}</span>
                      </div>
                    )}
                    {vehicle.mileage !== undefined && vehicle.mileage !== null && (
                      <div className="wl4-detail-overview-item">
                        <span className="wl4-detail-overview-label">Quilometragem</span>
                        <span className="wl4-detail-overview-value">{formatMileage(vehicle.mileage)}</span>
                      </div>
                    )}
                    {vehicle.fuel && (
                      <div className="wl4-detail-overview-item">
                        <span className="wl4-detail-overview-label">Combustível</span>
                        <span className="wl4-detail-overview-value">{vehicle.fuel}</span>
                      </div>
                    )}
                    {vehicle.type !== 2 && vehicle.transmission && (
                      <div className="wl4-detail-overview-item">
                        <span className="wl4-detail-overview-label">Câmbio</span>
                        <span className="wl4-detail-overview-value">{vehicle.transmission}</span>
                      </div>
                    )}
                    {vehicle.color && (
                      <div className="wl4-detail-overview-item">
                        <span className="wl4-detail-overview-label">Cor</span>
                        <span className="wl4-detail-overview-value">{vehicle.color}</span>
                      </div>
                    )}
                    {vehicle.type !== 2 && vehicle.doors > 0 && (
                      <div className="wl4-detail-overview-item">
                        <span className="wl4-detail-overview-label">Portas</span>
                        <span className="wl4-detail-overview-value">{vehicle.doors}</span>
                      </div>
                    )}
                    <div className="wl4-detail-overview-item">
                      <span className="wl4-detail-overview-label">Tipo</span>
                      <span className="wl4-detail-overview-value">{getVehicleTypeLabel(vehicle.type)}</span>
                    </div>
                    {availableStores.length > 1 && vehicle.store && (() => {
                      const storeForVehicle = config.stores?.find((s: any) => s._id === vehicle.store);
                      return storeForVehicle ? (
                        <div className="wl4-detail-overview-item">
                          <span className="wl4-detail-overview-label">Loja</span>
                          <span className="wl4-detail-overview-value">{storeForVehicle.company}</span>
                        </div>
                      ) : null;
                    })()}
                  </div>

                  {vehicle?.statistics && (vehicle?.statistics?.financing || vehicle?.statistics?.favorites) ? (
                    <div className="p-5">
                      {vehicle.statistics.financing > 0 && config.stores?.[0]?.ad?.displayNumberOfFinancingProposals && (
                        <span className="wl4-detail-stats-item">
                          <FilePen className="h-4 w-4" />
                          {vehicle.statistics.financing > 1
                            ? `${vehicle.statistics.financing} propostas em análise`
                            : `${vehicle.statistics.financing} proposta em análise`}
                        </span>
                      )}
                      {vehicle.statistics.favorites > 0 && vehicleStore?.ad?.displayNumberOfInterestedCustomers && (
                        <span className="wl4-detail-stats-item pl-5">
                          <Heart className="h-4 w-4" />
                          {vehicle.statistics.favorites > 1
                            ? `${vehicle.statistics.favorites} clientes interessados`
                            : `${vehicle.statistics.favorites} cliente interessado`}
                        </span>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Description */}
              {vehicle.description && (
                <div className="wl4-detail-panel">
                  <div className="wl4-detail-panel-head">
                    <h2 className="wl4-detail-panel-title">Descrição</h2>
                  </div>
                  <div className="wl4-detail-panel-body">
                    <p className="wl4-detail-desc">{vehicle.description}</p>
                  </div>
                </div>
              )}

              {/* Optionals */}
              {vehicle.optionals && vehicle.optionals.length > 0 && (
                <div className="wl4-detail-panel">
                  <div className="wl4-detail-panel-head">
                    <h2 className="wl4-detail-panel-title">Opcionais ({vehicle.optionals.length})</h2>
                  </div>
                  <div className="wl4-detail-panel-body">
                    <div className="wl4-detail-features">
                      {vehicle.optionals.map((optional: string, index: number) => (
                        <div key={index} className="wl4-detail-feature">
                          <span className="wl4-detail-feature-dot" />
                          <span>{optional}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Financing - inline */}
              {financingService && financingService.lead?.type === 'message' && (
                <div className="wl4-detail-panel">
                  <div className="wl4-detail-panel-body">
                    <ServiceModal
                      service={financingService}
                      serviceTitle="Simular Financiamento"
                      config={config}
                      renderInline={true}
                      preSelectedVehicle={vehicle}
                    />
                  </div>
                </div>
              )}

              {financingService && (financingService.lead?.type === 'api' || financingService.lead?.type === 'email') && (
                <div className="wl4-detail-panel">
                  <div className="wl4-detail-panel-body">
                    <LeadForm
                      vehicle={vehicle}
                      config={config}
                      buttonColor={buttonColor}
                      store={vehicleStore}
                      serviceType="65dcb7898eca108d87c91dbc"
                      serviceTitle="Simular Financiamento"
                      service={financingService}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <aside className="wl4-detail-sidebar">
              <div className="wl4-detail-dealer">
                <div className="wl4-detail-dealer-head">
                  <div className="wl4-detail-dealer-avatar">
                    <Building2 className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="wl4-detail-dealer-name">{vehicleStore?.company || config.name}</h3>
                    <p className="wl4-detail-dealer-role">Proprietário do anúncio</p>
                  </div>
                </div>

                {vehicleStore?.place?.address && (
                  <div className="wl4-detail-dealer-address">
                    <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{vehicleStore.place.address}</span>
                    {availableStores.length > 1 && userLocation && (() => {
                      const distance = getDistanceToStore(userLocation, vehicleStore);
                      return distance !== null ? (
                        <Badge
                          variant="secondary"
                          className="ml-auto flex items-center gap-1 px-2 py-0.5 text-white font-medium shrink-0"
                          style={{ backgroundColor: primaryColor }}
                        >
                          <Navigation className="h-3 w-3" />
                          <span className="text-xs">{formatDistance(distance)}</span>
                        </Badge>
                      ) : null;
                    })()}
                  </div>
                )}

                <div className="wl4-detail-cta-stack">
                  {(vehicleStore.ad?.lead?.type === 'api' || vehicleStore.ad?.lead?.type === 'email') ? (
                    <LeadForm
                      vehicle={vehicle}
                      config={config}
                      buttonColor={buttonColor}
                      store={vehicleStore}
                      serviceType="message"
                      serviceTitle="Envie uma mensagem"
                      service={{ lead: vehicleStore.ad.lead }}
                    />
                  ) : (
                    <Button
                      className="w-full text-white hover:shadow-glow transition-all duration-300"
                      style={{
                        background: `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}ee, ${buttonColor})`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`;
                      }}
                      onClick={handleWhatsApp}
                      size="lg"
                    >
                      <MessageCircle className="h-5 w-5 mr-2" />
                      Enviar mensagem
                    </Button>
                  )}

                  <Button
                    onClick={() => {
                      if (vehicleStore?.whatsapp?.ios) {
                        openWhatsApp(vehicleStore.whatsapp?.ios);
                      }
                    }}
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                    size="lg"
                  >
                    <MessageCircle className="h-5 w-5 mr-2" />
                    WhatsApp
                  </Button>

                  <Button
                    onClick={() => {
                      if (vehicleStore?.phone?.ios) {
                        window.open(vehicleStore.phone?.ios, '_blank');
                      }
                    }}
                    className="w-full text-white"
                    style={{
                      background: `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}ee, ${buttonColor})`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`;
                    }}
                    size="lg"
                  >
                    <Phone className="h-5 w-5 mr-2" />
                    Ligar agora
                  </Button>

                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full border-2"
                    style={{
                      borderColor: `${primaryColor}40`,
                      color: primaryColor,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = primaryColor;
                      e.currentTarget.style.backgroundColor = `${primaryColor}10`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = `${primaryColor}40`;
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    onClick={() => {
                      const place = vehicleStore?.place;
                      openStoreDirections(place);
                    }}
                  >
                    <MapPin className="h-5 w-5 mr-2" />
                    Como chegar
                  </Button>
                </div>
              </div>

              {showTestDrive && (
                <div className="wl4-detail-panel">
                  <div className="wl4-detail-panel-body wl4-detail-testdrive">
                    <h3 className="wl4-detail-testdrive-title">
                      <Gauge className="h-5 w-5 text-[var(--wl4-accent)]" />
                      Test-Drive
                    </h3>
                    <ServiceModal
                      service={testDriveService}
                      serviceTitle="Test-Drive"
                      config={config}
                      preSelectedVehicle={vehicle}
                    >
                      <Button
                        className="w-full text-white"
                        style={{
                          background: `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}ee, ${buttonColor})`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`;
                        }}
                      >
                        Agendar test-drive
                      </Button>
                    </ServiceModal>
                    <p className="wl4-detail-testdrive-desc">
                      Agende um test drive e experimente o veículo dos seus sonhos
                    </p>
                  </div>
                </div>
              )}

              <div
                className="wl4-detail-guarantee"
                style={{
                  background: `linear-gradient(135deg, ${secondaryColor}, ${secondaryColor}dd)`,
                }}
              >
                <Shield className="h-10 w-10 mx-auto opacity-90" />
                <h3>Garantia e qualidade</h3>
                <p>
                  Todos os nossos veículos passam por rigorosa inspeção e vêm com garantia.
                </p>
              </div>
            </aside>
          </div>

          {/* Related Vehicles */}
          {vehicleStore?.ad?.showRelatedOffers && relatedVehicles.length > 0 && (
            <section className="wl4-detail-related">
              <div className="wl4-section-header">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-wider text-[var(--wl4-accent)] mb-1">
                    Veículos similares
                  </p>
                  <h2 className="text-2xl md:text-3xl font-bold text-[var(--wl4-text)]">
                    Você também pode gostar
                  </h2>
                </div>
              </div>
              <div className="wl4-detail-related-grid">
                {relatedVehicles.slice(0, 4).map((relatedVehicle) => (
                  <LayoutVehicleCard key={relatedVehicle.id} vehicle={relatedVehicle} />
                ))}
              </div>
            </section>
          )}

          {/* Warning */}
          {config.stores?.[0]?.ad?.warning && (
            <div className="wl4-detail-warning">
              <h3>Aviso</h3>
              <p>{config.stores?.[0]?.ad?.warning}</p>
            </div>
          )}
        </div>
      </section>

      <Footer config={config} />

      {/* Fullscreen Gallery Modal */}
      {isFullscreen && (
        <div
          className="fixed inset-0 bg-black z-[100] flex items-center justify-center"
          onClick={closeFullscreen}
        >
          {/* Close Button */}
          <button
            onClick={closeFullscreen}
            className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
          >
            <X className="h-6 w-6 text-white" />
          </button>

          {/* Image Counter */}
          <div className="absolute top-4 left-4 bg-black/70 text-white px-4 py-2 rounded-lg text-sm z-10">
            {fullscreenImageIndex + 1} / {vehicle.photos.length}
          </div>

          {/* Main Image */}
          <div
            className="relative w-full h-full flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Loading Spinner */}
            {isFullscreenImageLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-30">
                <div className="animate-spin rounded-full h-20 w-20 border-b-2 border-white"></div>
              </div>
            )}

            <img
              src={vehicle.photos[fullscreenImageIndex]}
              alt={`${vehicle.brand} ${vehicle.model}`}
              className={`max-w-full max-h-full object-contain transition-opacity duration-300 ${isFullscreenImageLoading ? 'opacity-50' : 'opacity-100'}`}
              onLoad={handleFullscreenImageLoad}
            />

            {/* Navigation Arrows */}
            {vehicle.photos.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    prevFullscreenImage();
                  }}
                  disabled={isFullscreenImageLoading}
                  className={`absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/70 hover:bg-black/90 text-white p-4 rounded-full transition-colors ${isFullscreenImageLoading ? 'cursor-not-allowed opacity-30' : ''}`}
                >
                  <ChevronLeft className="h-8 w-8" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    nextFullscreenImage();
                  }}
                  disabled={isFullscreenImageLoading}
                  className={`absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/70 hover:bg-black/90 text-white p-4 rounded-full transition-colors ${isFullscreenImageLoading ? 'cursor-not-allowed opacity-30' : ''}`}
                >
                  <ChevronRight className="h-8 w-8" />
                </button>
              </>
            )}
          </div>

          {/* Thumbnail Strip - Show only nearby thumbnails */}
          {vehicle.photos.length > 1 && (
            <div className="absolute bottom-4 mb-1 left-1/2 transform -translate-x-1/2 max-w-2xl w-full px-4">
              <div className="bg-black/50 backdrop-blur-sm rounded-lg p-3">
                <div className="flex gap-2 justify-center">
                  {(() => {
                    const totalPhotos = vehicle.photos.length;
                    const maxVisible = 8;

                    // If 7 or fewer photos, show all
                    if (totalPhotos <= maxVisible) {
                      return vehicle.photos.map((photo: string, index: number) => (
                        <button
                          key={index}
                          onClick={(e) => {
                            e.stopPropagation();
                            setFullscreenImageIndex(index);
                          }}
                          className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${index === fullscreenImageIndex
                            ? 'border-white scale-110'
                            : 'border-transparent hover:border-white/50 opacity-70 hover:opacity-100'
                            }`}
                        >
                          <img
                            src={photo}
                            alt={`Foto ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ));
                    }

                    // Show thumbnails around current image
                    const halfVisible = Math.floor(maxVisible / 2);
                    let startIndex = Math.max(0, fullscreenImageIndex - halfVisible);
                    let endIndex = Math.min(totalPhotos, startIndex + maxVisible);

                    // Adjust if we're near the end
                    if (endIndex - startIndex < maxVisible) {
                      startIndex = Math.max(0, endIndex - maxVisible);
                    }

                    return vehicle.photos.slice(startIndex, endIndex).map((photo: string, index: number) => {
                      const actualIndex = startIndex + index;
                      return (
                        <button
                          key={actualIndex}
                          onClick={(e) => {
                            e.stopPropagation();
                            setFullscreenImageIndex(actualIndex);
                          }}
                          className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${actualIndex === fullscreenImageIndex
                            ? 'border-white scale-100'
                            : 'border-transparent hover:border-white/50 opacity-70 hover:opacity-100'
                            }`}
                        >
                          <img
                            src={photo}
                            alt={`Foto ${actualIndex + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Instructions - Hidden on mobile */}
          <div className="hidden md:block absolute bottom-20 mb-5 left-1/2 transform -translate-x-1/2 text-white/60 text-sm">
            Use as setas ← → do teclado ou clique nas setas para navegar | ESC para fechar
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleDetail;